import type { CRMNote } from "@/types";
import { NATIONAL_SECTION_LEGACY_ID } from "@/pages/people/membership-services/committees/constants";
import {
  formatSectionDisplayName,
  mapGovernanceStatus
} from "@/pages/facilities/[facilityId]/people/governance/_utils";
import type { GovernanceTitle } from "@/pages/facilities/[facilityId]/people/governance/types";
import type { PersonFacilityGovernanceRoleNode, PersonGovernanceRoleNode } from "./queries";
import type {
  PersonGovernanceOrgType,
  PersonGovernanceRole,
  PersonGovernanceSection,
  PersonGovernanceTarget
} from "./types";

type MappedPersonGovernanceRoleNode = {
  id: string;
  status: string;
  title: GovernanceTitle;
  target: PersonGovernanceTarget;
  notes: CRMNote[];
};

export {
  formatGovernanceRole,
  formatSectionDisplayName,
  getGovernanceStatusLabel,
  getGovernanceStatusTagType,
  getGovernanceStatusTagStyle,
  getEffectiveGovernanceSearch,
  clearGovernanceFilters,
  mapGovernanceStatus,
  matchesGovernanceRoleFilter,
  buildGovernanceRoleFilterOptions,
  GOVERNANCE_ORG_TYPE_FILTER_PARAM,
  parseGovernanceRoleFilterParam
} from "@/pages/facilities/[facilityId]/people/governance/_utils";

export const PERSON_GOVERNANCE_ORG_TYPE_LABELS: Record<PersonGovernanceOrgType, string> = {
  facility: "Facility",
  section: "Section",
  national: "National"
};

export function getPersonGovernanceOrgType(
  target: PersonGovernanceTarget | null | undefined
): PersonGovernanceOrgType | null {
  if (!target) {
    return null;
  }

  if (target.__typename === "BackofficeFacility") {
    return "facility";
  }

  if (target.__typename === "BackofficeSection") {
    return target.legacyId === NATIONAL_SECTION_LEGACY_ID ? "national" : "section";
  }

  return null;
}

export function mapFacilityGovernanceRoleNode(node: PersonFacilityGovernanceRoleNode): PersonGovernanceRole | null {
  return mapPersonGovernanceRole({
    id: node.id,
    status: node.status,
    title: node.title,
    notes: node.notes,
    target: {
      __typename: "BackofficeFacility",
      id: node.facilityId,
      name: node.facilityName,
      legacyId: node.facilityLegacyId,
      section:
        node.facilitySectionId && node.facilitySectionName
          ? { id: node.facilitySectionId, name: node.facilitySectionName }
          : null
    }
  });
}

export function mergePersonGovernanceRoleSources({
  facilityGovernanceRoles = [],
  governanceRoles = []
}: {
  facilityGovernanceRoles?: PersonFacilityGovernanceRoleNode[];
  governanceRoles?: PersonGovernanceRoleNode[];
}): PersonGovernanceRole[] {
  const byId = new Map<string, PersonGovernanceRole>();

  for (const node of facilityGovernanceRoles) {
    const mapped = mapFacilityGovernanceRoleNode(node);
    if (mapped) {
      byId.set(mapped.id, mapped);
    }
  }

  for (const node of governanceRoles) {
    if (byId.has(node.id)) {
      continue;
    }

    const mapped = mapSectionGovernanceRoleNode(node);
    if (mapped) {
      byId.set(mapped.id, mapped);
    }
  }

  return [...byId.values()];
}

export function mapSectionGovernanceRoleNode(node: PersonGovernanceRoleNode): PersonGovernanceRole | null {
  if (!node.sectionId || !node.sectionName) {
    return null;
  }

  return mapPersonGovernanceRole({
    id: node.id,
    status: node.status,
    title: node.title,
    notes: node.notes,
    target: {
      __typename: "BackofficeSection",
      id: node.sectionId,
      name: node.sectionName,
      legacyId: node.sectionLegacyId
    }
  });
}

export function mapPersonGovernanceRole(node: MappedPersonGovernanceRoleNode): PersonGovernanceRole | null {
  const orgType = getPersonGovernanceOrgType(node.target);
  if (!orgType) {
    return null;
  }

  if (node.target.__typename === "BackofficeFacility") {
    return {
      id: node.id,
      status: mapGovernanceStatus(node.status),
      title: node.title,
      notes: node.notes,
      orgType,
      organizationName: node.target.name,
      organizationSecondaryText: formatSectionDisplayName(node.target.section?.name),
      facility: {
        id: node.target.id,
        name: node.target.name,
        legacyId: node.target.legacyId,
        section: node.target.section
      }
    };
  }

  return {
    id: node.id,
    status: mapGovernanceStatus(node.status),
    title: node.title,
    notes: node.notes,
    orgType,
    organizationName: node.target.name,
    organizationSecondaryText: PERSON_GOVERNANCE_ORG_TYPE_LABELS[orgType],
    section: {
      id: node.target.id,
      name: node.target.name,
      legacyId: node.target.legacyId
    }
  };
}

export function matchesPersonGovernanceSearch(role: PersonGovernanceRole, searchTerm: string): boolean {
  const normalizedSearch = searchTerm.trim().toLowerCase();

  if (!normalizedSearch) {
    return true;
  }

  const searchableValues = [
    role.organizationName,
    role.organizationSecondaryText,
    PERSON_GOVERNANCE_ORG_TYPE_LABELS[role.orgType],
    role.facility?.name,
    role.facility?.section?.name,
    formatSectionDisplayName(role.facility?.section?.name),
    role.facility?.legacyId,
    role.section?.name,
    role.section?.legacyId,
    role.title.title
  ];

  return searchableValues.some((value) => value?.toLowerCase().includes(normalizedSearch));
}

export function matchesPersonGovernanceOrgTypeFilter(
  role: PersonGovernanceRole,
  selectedOrgTypes: PersonGovernanceOrgType[]
): boolean {
  if (selectedOrgTypes.length === 0) {
    return true;
  }

  return selectedOrgTypes.includes(role.orgType);
}

export function buildPersonGovernanceOrgTypeFilterOptions(roles: PersonGovernanceRole[]): {
  value: PersonGovernanceOrgType;
  label: string;
}[] {
  const present = new Set(roles.map((role) => role.orgType));

  return (Object.keys(PERSON_GOVERNANCE_ORG_TYPE_LABELS) as PersonGovernanceOrgType[])
    .filter((orgType) => present.has(orgType))
    .map((orgType) => ({ value: orgType, label: PERSON_GOVERNANCE_ORG_TYPE_LABELS[orgType] }));
}

export function isFacilityPersonGovernanceRole(role: PersonGovernanceRole): boolean {
  return role.orgType === "facility";
}

export function getPersonGovernanceSectionCatalog(roles: PersonGovernanceRole[]): PersonGovernanceSection[] {
  const byId = new Map<string, PersonGovernanceSection>();

  for (const role of roles) {
    if (isFacilityPersonGovernanceRole(role)) {
      continue;
    }

    const section = role.section;
    if (!section?.id || !section.name || byId.has(section.id)) {
      continue;
    }

    byId.set(section.id, {
      id: section.id,
      name: section.name,
      legacyId: section.legacyId
    });
  }

  return [...byId.values()];
}
